import {EditorToolType, LayerId, EditorLayersMode, ViewerToolType, ExtendedEditorToolDefinition, ExtendedEditorToolType} from '@mc/react-mapio';
import {OVERLAY_LAYER_ID} from '../const';
import {LayerVO} from '../api';

const ALWAYS_ALLOWED_VIEWER_TOOLS = new Set<string>(Object.values(ViewerToolType));

const isAlwaysAllowedTool = (toolType: string): boolean => ALWAYS_ALLOWED_VIEWER_TOOLS.has(toolType);

const SELECT_TOOLS = new Set<EditorToolType>([
    EditorToolType.SELECT,
    EditorToolType.EXACT_SELECT,
    EditorToolType.EXACT_SINGLE_SELECT,
    EditorToolType.BOX_SELECT,
    EditorToolType.LINE_SELECT,
    EditorToolType.POLY_SELECT,
    EditorToolType.POLY_SELECT_INTERSECT,
    EditorToolType.CIRCLE_SELECT,
]);

export const isSelectTool = (toolType: EditorToolType | null | undefined): boolean => SELECT_TOOLS.has(toolType as EditorToolType);

export const isToolEnabled = (
    toolType: string,
    toolsAllowedByServer: string[] | undefined,
    layerId: any,
    userActionTypes: {id: any; actionType: string[]}[] | undefined,
    isIframe: boolean,
    serverPublic: boolean,
    layersMode?: EditorLayersMode,
): boolean => {
    // Viewer nástroje jsou povoleny vždy
    if (isAlwaysAllowedTool(toolType)) {
        return true;
    }

    // Vlastní akce (CUSTOM_ACTION) definuje nadřazená aplikace, mapový engine o nich neví
    // a tak je nikdy neposílá v seznamu povolených nástrojů. Necháváme je proto vždy povolené.
    if (toolType === EditorToolType.CUSTOM_ACTION) return true;

    let enabled = true;
    const filterActive = Array.isArray(toolsAllowedByServer) && toolsAllowedByServer.length > 0;
    if (filterActive) {
        enabled = toolsAllowedByServer.includes(toolType);
    }

    // při embed nebo veřejném módu neřešíme práva vrstev
    if (isIframe || serverPublic) return enabled;

    // V režimu OVERLAY povolíme všechny editační nástroje pro objekty z OVERLAY vrstvy
    // Pro objekty z jiných vrstev povolíme pouze COPY operace
    if (layersMode === EditorLayersMode.OVERLAY) {
        // COPY operace jsou povoleny vždy v OVERLAY režimu (pro jakoukoliv vrstvu)
        if (toolType === EditorToolType.COPY || toolType === EditorToolType.COPY_TO_CLIPBOARD) {
            return enabled;
        }
        // Ostatní editační nástroje jsou v OVERLAY režimu povoleny
        // (ale budou fungovat pouze na objektech z OVERLAY vrstvy díky writableSelect)
        // return enabled;
    }

    // V režimu OVERLAY povolíme všechny editační nástroje pro objekty z OVERLAY vrstvy
    // Pro objekty z jiných vrstev povolíme pouze COPY operace
    if (layersMode === EditorLayersMode.OVERLAY) {
        // COPY operace jsou povoleny vždy v OVERLAY režimu (pro jakoukoliv vrstvu)
        if (toolType === EditorToolType.COPY || toolType === EditorToolType.COPY_TO_CLIPBOARD) {
            return enabled;
        }
        // Ostatní editační nástroje jsou v OVERLAY režimu povoleny
        // (ale budou fungovat pouze na objektech z OVERLAY vrstvy díky writableSelect)
        // return enabled;
    }

    // V režimu OVERLAY povolíme všechny editační nástroje pro objekty z OVERLAY vrstvy
    // Pro objekty z jiných vrstev povolíme pouze COPY operace
    if (layersMode === EditorLayersMode.OVERLAY) {
        // COPY operace jsou povoleny vždy v OVERLAY režimu (pro jakoukoliv vrstvu)
        if (toolType === EditorToolType.COPY || toolType === EditorToolType.COPY_TO_CLIPBOARD) {
            return enabled;
        }
        // Ostatní editační nástroje jsou v OVERLAY režimu povoleny
        // (ale budou fungovat pouze na objektech z OVERLAY vrstvy díky writableSelect)
        // return enabled;
    }

    if (!layerId) return enabled;

    const layerActionTypes = userActionTypes?.find(item => item.id === layerId);

    if (
        toolType === EditorToolType.COPY ||
        toolType === EditorToolType.SPLIT_LINE ||
        toolType === EditorToolType.SPLIT_POLYGON ||
        toolType === EditorToolType.PASTE ||
        toolType === EditorToolType.MULTI_ADD_EXISTING ||
        toolType === EditorToolType.ADD_TO_COLLECTION ||
        toolType === EditorToolType.MULTI_FIXED_RECTANGLE
    ) {
        return enabled && !!layerActionTypes?.actionType.includes('INSERT');
    }

    if (toolType === EditorToolType.DELETE || toolType === EditorToolType.REMOVE_FROM_COLLECTION) {
        return enabled && !!layerActionTypes?.actionType.includes('DELETE');
    }

    if (
        toolType === EditorToolType.EDIT ||
        toolType === EditorToolType.MOVE ||
        toolType === EditorToolType.OFFSET ||
        toolType === EditorToolType.ROTATE ||
        toolType === EditorToolType.DUPLICATE ||
        toolType === EditorToolType.CUT ||
        toolType === EditorToolType.LINE_EXTENSION ||
        toolType === EditorToolType.CUTOUT_DRAW ||
        toolType === EditorToolType.CUTOUT_FIXED_RECTANGLE ||
        toolType === EditorToolType.CUTOUT_FROM_EXISTING ||
        toolType === EditorToolType.CUTOUT_REMOVE ||
        toolType === EditorToolType.CUTOUT_PASTE
    ) {
        return enabled && !!layerActionTypes?.actionType.includes('UPDATE');
    }

    return enabled;
};

export const computeEnabledTools = (
    allTypes: string[],
    toolsEnabled: string[] | undefined,
    selectedDetailFeature: any[] | undefined,
    userActionTypes: {id: any; actionType: string[]}[] | undefined,
    isIframe: boolean,
    serverPublic: boolean,
    layersMode?: EditorLayersMode,
): string[] => {
    const filterActive = Array.isArray(toolsEnabled) && toolsEnabled.length > 0;
    const result: string[] = [];

    for (const type of allTypes) {
        // Viewer nástroje jsou povoleny vždy bez ohledu na serverový filtr
        if (isAlwaysAllowedTool(type)) {
            result.push(type);
            continue;
        }

        // Vlastní akce nadřazené aplikace serverový filtr neřeší, jsou vždy povolené
        if (type === EditorToolType.CUSTOM_ACTION) {
            result.push(type);
            continue;
        }

        // Pokud je aktivní serverový filtr, a typ není v něm, přeskočíme
        if (filterActive && !toolsEnabled.includes(type)) continue;

        // Kopírování je dostupné pouze pokud je vybrána právě jedna feature
        if (type === EditorToolType.COPY && selectedDetailFeature?.length !== 1) {
            continue;
        }

        // Pokud existuje vybraný feature, použijeme jeho vrstvu pro kontrolu práv
        if (selectedDetailFeature && selectedDetailFeature.length > 0) {
            const selectedFeature = selectedDetailFeature[0];
            const layerId = selectedFeature.parentLayerId ?? selectedFeature.layerId;
            if (isToolEnabled(type, toolsEnabled, layerId, userActionTypes, isIframe, serverPublic, layersMode)) {
                result.push(type);
            }
        }
        // Pokud není vybraný žádný feature, necháme rozhodnutí pouze na serverovém filtru a režimu
        else if (isToolEnabled(type, toolsEnabled, null, userActionTypes, isIframe, serverPublic, layersMode)) {
            result.push(type);
        }
    }
    return Array.from(new Set(result));
};

export const canDeleteFeatures = (
    layerIds: LayerId[],
    userActionTypes: {id: LayerId; actionType: string[]}[],
    serverPublic: boolean,
    isIframe: boolean,
): boolean => {
    // pokud jsou všechny layerIds jen OVERLAY_LAYER_ID
    if (layerIds.length > 0 && layerIds.every(id => id === OVERLAY_LAYER_ID)) {
        return true;
    }
    if (serverPublic || isIframe) {
        return false;
    }
    return layerIds.every(layerId => {
        if (OVERLAY_LAYER_ID === layerId) return true; // pro overlay vrstvu neřešíme práva, vždy povolíme smazání
        const actionTypes = userActionTypes.find(uat => uat.id?.toString() === layerId?.toString());
        return actionTypes?.actionType.includes('DELETE');
    });
};

type ToolItem = string | ExtendedEditorToolDefinition;

/**
 * Odstraní přebytečné SEPARATOR položky (duplicitní, na začátku, na konci).
 */
const deduplicateSeparators = (items: ToolItem[]): ToolItem[] => {
    const result: ToolItem[] = [];
    for (const item of items) {
        if (item === 'SEPARATOR') {
            if (result.length > 0 && result[result.length - 1] !== 'SEPARATOR') {
                result.push(item);
            }
        } else {
            result.push(item);
        }
    }
    if (result[result.length - 1] === 'SEPARATOR') result.pop();
    return result;
};

/**
 * Filtruje pole nástrojů (včetně vnořených) podle seznamu povolených typů.
 * Zachovává původní strukturu (MENU, GROUP, …).
 *
 * Pravidla:
 * - String položky: propustit pokud je typ v enabledTypes (SEPARATOR vždy projde).
 * - Objektové položky s polem `id`: vždy propustit (kontejnery / speciální nástroje),
 *   ale rekurzivně filtrovat jejich `items`.
 * - Objektové položky bez `id`: filtrovat podle `type`.
 */
export const filterToolsByEnabled = (
    tools: ToolItem[],
    enabledTypes: string[],
    insertionLayerId?: LayerId,
    layersConfigData?: {
        [key: number]: LayerVO;
    },
): ToolItem[] => {
    const enabledSet = new Set(enabledTypes);

    // Zjistíme allowedGeometry pro insertionLayer – stejná logika jako v ToolbarDetail.isToolAllowed
    const insertionLayerDef = insertionLayerId != null ? layersConfigData?.[insertionLayerId as number] : undefined;
    const allowedGeometry = insertionLayerDef?.allowedGeometry;

    /**
     * Zda má být daný geometrický tool povolen.
     * Replikuje logiku ToolbarDetail.isToolAllowed + isEnabledForTool:
     * - isToolAllowed: pokud allowedGeometry není definována → vše povoleno, jinak dle includes
     * - isEnabledForTool: tool musí být v enabledSet (= computedToolsEnabled, tj. i serverová práva)
     */
    const isGeometryToolAllowed = (toolType: string): boolean => {
        // isEnabledForTool – musí projít serverovým filtrem / právy
        if (!enabledSet.has(toolType)) return false;
        // isToolAllowed – pokud není allowedGeometry, vše povoleno
        if (!allowedGeometry) return true;
        if (toolType === EditorToolType.POINT) return allowedGeometry.includes('POINT' as any);
        if (toolType === EditorToolType.LINE) return allowedGeometry.includes('LINE' as any);
        if (toolType === EditorToolType.POLYGON) return allowedGeometry.includes('POLYGON' as any);
        return true;
    };

    /** Geometry tools které mohou být vloženy do NEW_ITEM jako items pro klienta */
    const allowedGeometryItems = (): string[] => {
        return [EditorToolType.POINT, EditorToolType.LINE, EditorToolType.POLYGON].filter(isGeometryToolAllowed);
    };

    const filterItems = (items: ToolItem[]): ToolItem[] => {
        const result: ToolItem[] = [];

        for (const item of items) {
            if (typeof item === 'string') {
                if (item === 'SEPARATOR' || enabledSet.has(item)) {
                    result.push(item);
                }
            } else if (item && typeof item === 'object') {
                const hasId = (item as any).id !== undefined;
                const itemType: string = (item as any).type;
                const subItems: ToolItem[] | undefined = (item as any).items;

                if (hasId) {
                    // NEW_ITEM: vložíme povolené geometry tools jako items, aby je klient
                    // (setEditorTools) zpracoval a přidal do editorTools (pro kl. zkratky).
                    // ToolbarDetail ignoruje items a vždy používá newItemToolbarChildDef interně.
                    if (itemType === ExtendedEditorToolType.NEW_ITEM) {
                        const geoItems = allowedGeometryItems();
                        const injectedItems = [...(subItems ?? []), ...geoItems];
                        result.push({...item, items: injectedItems} as ExtendedEditorToolDefinition);
                    } else if (subItems) {
                        result.push({...item, items: filterItems(subItems)} as ExtendedEditorToolDefinition);
                    } else {
                        result.push(item);
                    }
                } else if (enabledSet.has(itemType)) {
                    if (subItems) {
                        result.push({...item, items: filterItems(subItems)} as ExtendedEditorToolDefinition);
                    } else {
                        result.push(item);
                    }
                }
            }
        }

        return deduplicateSeparators(result);
    };

    return filterItems(tools);
};
